import { yupResolver } from '~/helpers/yupResolver';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
// import LogoIcon from '~/brand/gfactor.com/logo-icon';
// import LogoWhite from '~/brand/gfactor.com/logo-white';
import { Link } from 'react-router-dom';
import { BrandAuthLogo, BrandSquare } from '~/brand/BrandContext';
import { Input } from '~/components/form/Input';
import { post } from '~/helpers/fetch';
import { validateRedirect } from '~/helpers/redirect';
import { clearReferralCode, getReferralCode } from '~/helpers/referral';
import { CheckboxState } from '../ui/nucleus/Checkbox';
import { Checkbox } from '../ui/nucleus/Checkbox/Checkbox';
import { isDuplicateEmailError, normalizeSignupError } from './signupError';

type FormData = {
  name: string;
  email: string;
  password: string;
  tnc_accepted: boolean;
  timeZone?: string;
  referralCode?: string;
};

type SignupResult = {
  email: string;
  redirectTo?: string;
};

export function SignupForm() {
  const { t } = useTranslation('auth');
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Check for referral code on component mount
  useEffect(() => {
    const code = getReferralCode();
    if (code) {
      setReferralCode(code);
    }
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required('Required'),
        email: Yup.string().required('Required'),
        password: Yup.string().required('Required'),
        tnc_accepted: Yup.boolean()
          .transform((value) => {
            if (value === '' || value === null || value === undefined) {
              return false;
            }
            return Boolean(value);
          })
          .typeError(t('mustAcceptTerms'))
          .oneOf([true], t('mustAcceptTerms'))
          .required(t('mustAcceptTerms')),
      }),
    [t]
  );

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    control,
    setError,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      tnc_accepted: false,
    },
  });

  const signup = async (values: FormData) => {
    try {
      // Get browser timezone and include it in the signup payload
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Retrieve and validate referral code from localStorage
      const referralCode = getReferralCode();

      const signupData = {
        ...values,
        timeZone: browserTimezone,
        ...(referralCode && { referralCode }), // Only include if referralCode exists
      };

      const { data } = await post<SignupResult>('/api/signup', signupData);

      // Clean up referral code from localStorage after successful signup
      clearReferralCode();

      if (data.redirectTo) {
        // Validate redirect to prevent open redirect vulnerabilities
        const safeRedirect = validateRedirect(data.redirectTo, '/');
        window.location.href = safeRedirect;
      } else {
        // After signup, redirect to onboarding for new users
        window.location.href = '/onboarding/chat';
      }
    } catch (err) {
      // Check if this is a duplicate email error
      // Backend returns: { error: "...", message: "...", code: "DUPLICATE_EMAIL" }
      // Safely normalize any thrown error (Error, FetchError, plain object, or primitive)
      const error = normalizeSignupError(err);
      const backendMessage = error.message; // Already extracted by fetch.ts

      if (isDuplicateEmailError(error)) {
        // Show specific error for duplicate email on the email field
        // Use the backend message if available, otherwise fallback to translation
        const emailFieldMessage =
          backendMessage ||
          t(
            'emailAlreadyRegistered',
            'This email is already registered. Please login instead.'
          );

        setError('email', {
          type: 'manual',
          message: emailFieldMessage,
        });

        // Also set a root error for a general message with login link
        setError('root.duplicate', {
          type: 'manual',
          message:
            backendMessage ||
            t('accountExists', 'An account with this email already exists.'),
        });
      } else {
        // For other errors, show the backend message or a general error
        const displayMessage =
          backendMessage && backendMessage !== 'Bad Request'
            ? backendMessage
            : t(
                'signupError',
                'Unable to create account. Please check your information and try again, or contact support if the issue persists.'
              );

        setError('root.failure', {
          type: 'manual',
          message: displayMessage,
        });
      }

      // Error is handled, form will not stay disabled
      // react-hook-form will automatically reset isSubmitting
    }
  };

  return (
    <div className='flex h-screen w-screen items-center justify-center'>
      <div className='w-full lg:w-1/2'>
        <form
          onSubmit={handleSubmit(signup)}
          className='mx-auto flex flex-1 flex-col justify-center rounded-lg bg-white px-10 py-16 md:w-4/5 md:border md:border-gray88 lg:mx-auto lg:w-[30rem] lg:flex-initial'
        >
          <div className='mb-12 flex flex-col items-center justify-center'>
            <BrandSquare className='mx-auto h-20' />
            <h1 className='mt-5 text-center text-2xl font-medium'>
              {t('signUp')}
            </h1>
          </div>

          {/* Display referral code banner if present */}
          {referralCode && (
            <div className='mb-6 rounded-lg border border-outline-variant bg-green-50 p-4'>
              <div className='flex items-start gap-3'>
                <svg
                  className='mt-0.5 h-5 w-5 flex-shrink-0 text-green-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <div className='flex-1'>
                  <p className='text-sm font-medium text-green-800'>
                    {t('referralCodeApplied', 'Referral code applied')}
                  </p>
                  <p className='mt-1 text-sm text-green-700'>
                    Welcome aboard — your referral unlocks 50% off for 3 months
                    after your trial.
                  </p>
                </div>
              </div>
            </div>
          )}
          <Input
            {...register('name')}
            required
            label={t('name')}
            type='text'
            error={errors.name?.message}
          />
          <Input
            {...register('email')}
            required
            label={t('emailAddress')}
            type='email'
            error={errors.email?.message}
          />
          <Input
            {...register('password')}
            required
            label={t('password')}
            type='password'
            error={errors.password?.message}
          />
          <div className='mb-4'>
            <Controller
              name='tnc_accepted'
              control={control}
              render={({ field }) => (
                <label className='flex cursor-pointer items-start gap-2'>
                  <Checkbox
                    value={
                      field.value
                        ? CheckboxState.CHECKED
                        : CheckboxState.UNCHECKED
                    }
                    onClick={(_, newState) => {
                      field.onChange(newState === CheckboxState.CHECKED);
                    }}
                  />
                  <span className='text-sm'>
                    {t('i_accept_the_terms_of_service')}{' '}
                    <a
                      href='https://40921248.fs1.hubspotusercontent-ap1.net/hubfs/40921248/Legal%20and%20Compliance/PropFit%20Terms%20and%20Conditions%20v1.pdf'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='underline hover:text-brand'
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('termsAndConditions', 'Terms and Conditions')}
                    </a>
                  </span>
                </label>
              )}
            />
            {errors.tnc_accepted?.message && (
              <p className='mt-1 text-sm text-red-600'>
                {errors.tnc_accepted.message}
              </p>
            )}
          </div>

          {/* Display root-level errors (duplicate email or general errors) */}
          {(errors.root?.duplicate || errors.root?.failure) && (
            <div className='mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800'>
              {errors.root.duplicate?.message || errors.root.failure?.message}
              {errors.root?.duplicate && (
                <div className='mt-2'>
                  <Link
                    to='/login'
                    className='font-semibold underline hover:text-red-900'
                  >
                    {t('goToLogin', 'Go to Login')}
                  </Link>
                </div>
              )}
            </div>
          )}

          <button
            className='mb-4 mt-8 w-full rounded-lg bg-brand p-3 font-semibold uppercase text-white transition-all duration-300 hover:bg-slate-dark  disabled:opacity-50'
            disabled={isSubmitting}
          >
            {t('createAccount')}
          </button>
          <div className='text-center'>
            <Link className='font-normal underline' to='/login'>
              {t('backToLogin')}
            </Link>
          </div>
        </form>
      </div>

      <div
        className={
          'hidden h-screen items-center justify-center bg-gradient-to-b from-brand to-slate-dark lg:flex lg:w-1/2'
        }
      >
        <BrandAuthLogo className='w-1/2' />
      </div>
    </div>
  );
}
